<template>
    <!-- Background element -->
    <div
        v-if="entry?.backgroundElement_Image"
        aria-hidden="true"
        class="pointer-events-none absolute inset-0 flex h-full w-full"
        :class="[
            bgOpacity,
            {
                'items-center': ['left', 'center', 'right'].includes(entry.backgroundElement_Placement),
                'items-end': ['bottom-left', 'bottom', 'bottom-right'].includes(entry.backgroundElement_Placement),
                'items-start': ['top-left', 'top', 'top-right'].includes(entry.backgroundElement_Placement),
                'justify-start': ['top-left', 'left', 'bottom-left'].includes(entry.backgroundElement_Placement),
                'justify-end': ['top-right', 'right', 'bottom-right'].includes(entry.backgroundElement_Placement),
                'justify-center': ['top', 'center', 'bottom'].includes(entry.backgroundElement_Placement),
            },
        ]"
    >
        <div
            v-if="isSvg"
            v-element-visibility="onElementVisibility"
            class="relative"
            :class="[
                bgSize,
                {
                    'w-full': entry.backgroundElement_ScaleWidth === true,
                    'h-full': entry.backgroundElement_ScaleHeight === true,
                    'object-cover':
                        entry.backgroundElement_ScaleWidth === true && entry.backgroundElement_ScaleHeight === true,
                },
            ]"
            :style="{
                transform: `translate(${horizontalOffset}, ${verticalOffset})`,
            }"
            v-html="isVisible ? svgContent : ''"
        />

        <img
            v-else
            :src="backgroundImageUrl"
            :class="[
                bgSize,
                {
                    'w-full': entry.backgroundElement_ScaleWidth === true,
                    'h-full': entry.backgroundElement_ScaleHeight === true,
                    'object-cover':
                        entry.backgroundElement_ScaleWidth === true && entry.backgroundElement_ScaleHeight === true,
                },
            ]"
            class="relative"
            :style="{
                transform: `translate(${horizontalOffset}, ${verticalOffset})`,
                objectPosition: `${entry?.backgroundElement_Image?.[0]?.focalPoint[0] * 100 ?? '50'}% ${entry?.backgroundElement_Image?.[0]?.focalPoint[1] * 100 ?? '50'}%`,
            }"
        >
    </div>
</template>

<script setup>
    import { vElementVisibility } from '@vueuse/components'

    const props = defineProps({
        entry: Object,
    })

    const svgContent = ref('')
    const isVisible = ref(false)

    // Computed property to dynamically determine the image URL
    const backgroundImageUrl = computed(() => {
        const image = props.entry?.backgroundElement_Image?.[0]
        if (!image) return null
        return image.width < 1920 ? image.urlOriginal : image.url
    })

    // Check if the mimeType contains 'svg'
    const isSvg = computed(() => {
        return props.entry?.backgroundElement_Image?.[0]?.mimeType?.includes('svg')
    })

    const onElementVisibility = (state) => {
        if (!state) {
            return
        }

        // once the element is visible then its always visible
        isVisible.value = state
    }

    onMounted(() => {
        nextTick(() => {
            if (isSvg.value) {
                fetch(props.entry.backgroundElement_Image[0].url)
                    .then((res) => res.text())
                    .then((svg) => {
                        // Set width and height to 100% for the SVG
                        const parser = new DOMParser()
                        const svgDoc = parser.parseFromString(svg, 'image/svg+xml')
                        const svgElement = svgDoc.querySelector('svg')
                        if (svgElement) {
                            svgElement.setAttribute('width', '100%')
                            svgElement.setAttribute('height', '100%')
                            svgContent.value = svgElement.outerHTML
                        }
                    })
            }
        })
    })

    // Other computed properties
    const bgSize = computed(() => {
        if (props.entry?.backgroundElement_ScaleWidth !== true && props.entry?.backgroundElement_ScaleHeight !== true) {
            switch (props.entry?.backgroundElement_Size) {
                case 'xs':
                    return 'h-[100px]'
                case 'sm':
                    return 'h-[200px]'
                case 'md':
                default:
                    return 'h-[300px]'
                case 'lg':
                    return 'h-[500px]'
                case 'xl':
                    return 'h-[800px]'
            }
        }
        return ''
    })

    const bgOpacity = computed(() => {
        switch (props.entry?.backgroundElement_Opacity) {
            case '5':
                return 'opacity-5'
            case '10':
                return 'opacity-10'
            case '20':
            default:
                return 'opacity-20'
            case '50':
                return 'opacity-50'
            case '100':
                return 'opacity-100'
        }
    })

    const getOffset = (value) => {
        if (!value) return '0'
        return value
    }

    const horizontalOffset = computed(() => getOffset(props.entry?.backgroundElement_HorizontalAdjustment))

    const verticalOffset = computed(() => getOffset(props.entry?.backgroundElement_VerticalAdjustment))
</script>
